import {
  MenuItem,
  TextField,
  TextFieldProps,
  Checkbox,
  ListItemText,
  Box,
} from '@mui/material';
import { hexWithOpacity } from 'common/utils';
import CustomLabel from 'components/common/CustomLabel/CustomLabel';
import CustomText from 'components/common/CustomText/CustomText';
import { Controller, Control, FieldError } from 'react-hook-form';
import { colors } from 'styles/colors';
import { fonts } from 'styles/fonts';

type SelectOption = {
  value: string | number;
  label: string;
};

type CustomFormMultiSelectProps = {
  name: string;
  control: Control<any>;
  error?: FieldError | any;
  placeholder?: string;
  options: SelectOption[];
  label?: string;
  required?: boolean;
  note?: string;
} & Omit<TextFieldProps, 'name' | 'error' | 'helperText' | 'select'>;

export default function CustomFormMultiSelect({
  name,
  control,
  error,
  placeholder,
  options,
  label,
  required,
  note,
  sx,
  ...rest
}: CustomFormMultiSelectProps) {
  return (
    <Box>
      {label && <CustomLabel
        label={label}
        note={note}
        required={required} />}
      <Controller
        name={name}
        control={control}
        defaultValue={[]}
        render={({ field }) => (
          <TextField
            select
            size="small"
            fullWidth
            {...rest}
            value={Array.isArray(field.value) ? field.value : []}
            onChange={(event) => {
              const value = event.target.value;
              field.onChange(Array.isArray(value) ? value : []);
            }}
            error={!!error}
            helperText={error?.message}
            slotProps={{
              formHelperText: { sx: { ml: 0 } },
              select: {
                multiple: true,
                displayEmpty: true,
                renderValue: (selected) => {
                  const values = Array.isArray(selected) ? selected : [];

                  if (values.length === 0 && placeholder) {
                    return (
                      <span
                        style={{
                          fontSize: '14px',
                          fontFamily: fonts.neue,
                          fontWeight: 500,
                          color: colors.secondary,
                        }}
                      >
                        {placeholder}
                      </span>
                    );
                  }

                  return values
                    .map(
                      (value) =>
                        options.find((o) => o.value === value)?.label
                    )
                    .filter(Boolean)
                    .join(', ');
                },
              },
            }}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.secondary,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.secondary,
              },
              '& .MuiSelect-select': {
                fontSize: '15px',
                fontFamily: fonts.neue,
                fontWeight: 500,
                color: colors.black,
              },
              ...sx,
            }}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox
                  checked={Array.isArray(field.value) &&
                    field.value.includes(option.value)}
                  size="small"
                />
                <CustomText text={option.label} fontSize={14} color={colors.black} />
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    </Box>

  );
}
